import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  InboxOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import SessionClassScopes from "../../lms/components/SessionClassScopes";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";
import { useLocation, useNavigate } from "react-router-dom";
import useAcademicYears from "../../hooks/common/useAcademicYears";
import useTerms from "../../hooks/common/useTerms";
import {
  createLmsApi,
  getLmsErrorMessage,
  getLmsListItems,
} from "../../lms/api";
import {
  LMS_SESSION_STATUSES,
  LMS_SESSION_TYPES,
  statusColor,
} from "../../lms/constants";
import {
  LmsErrorAlert,
  LmsPageHeader,
  formatDate,
  formatDateTime,
} from "../../lms/components/LmsPrimitives";

const { Text, Title } = Typography;

function sessionFormValues(record, academicYears = [], terms = []) {
  return record
    ? {
        title: record.title,
        session_type: record.session_type,
        acadyrID:
          record.acadyrID ||
          academicYears.find(
            (year) => String(year.acadyr) === String(record.academicYear),
          )?.id,
        termID:
          record.termID ||
          terms.find((term) => term.name === record.termName)?.id,
        sessionScope: record.sessionScope || "Whole School",
        classScopes: (record.classScopes || []).map((scope) => ({
          classID: scope.classID,
          streamIDs: [...(scope.streamIDs || [])],
        })),
        dates:
          record.start_date && record.end_date
            ? [dayjs(record.start_date), dayjs(record.end_date)]
            : undefined,
      }
    : { dates: undefined, sessionScope: "Whole School", classScopes: [] };
}

function sessionScopeDetails(record) {
  if (record.sessionScope !== "Selected Classes") return "Whole School";
  return (
    <Space direction="vertical" size={4}>
      {(record.classScopes || []).map((scope) => (
        <span key={scope.classID}>
          {scope.className || `Class ${scope.classID}`} ·{" "}
          {scope.streamIDs?.length
            ? scope.streamNames?.length
              ? scope.streamNames.join(", ")
              : scope.streamIDs.map((id) => `Stream ${id}`).join(", ")
            : "All streams"}
        </span>
      ))}
    </Space>
  );
}

function canArchive(record) {
  return ["Completed", "Inactive"].includes(record?.status);
}

const LmsSessions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useMemo(() => createLmsApi(navigate), [navigate]);
  const [form] = Form.useForm();
  const [activateForm] = Form.useForm();
  const [verifyForm] = Form.useForm();
  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const [sessions, setSessions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: undefined,
    session_type: undefined,
    academic_year: undefined,
    term: undefined,
    session_scope: undefined,
    school_class: undefined,
    stream: undefined,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activateRecord, setActivateRecord] = useState(null);
  const [verifyRecord, setVerifyRecord] = useState(null);
  const [uploadKeyResult, setUploadKeyResult] = useState(null);
  const [archiveRecord, setArchiveRecord] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [selectedScope, setSelectedScope] = useState("Whole School");

  const loadSessions = useCallback(
    async (nextPage = 1, nextPageSize = 20) => {
      setLoading(true);
      setError("");
      const response = await api.sessions.list({
        page: nextPage,
        pageSize: nextPageSize,
        search: filters.search || undefined,
        status: filters.status,
        session_type: filters.session_type,
        academic_year: filters.academic_year,
        term: filters.term,
        session_scope: filters.session_scope,
        school_class: filters.school_class,
        stream: filters.stream,
      });
      if (response?.ok) {
        const result = getLmsListItems(response.data);
        setSessions(result.items);
        setPagination({
          current: nextPage,
          pageSize: nextPageSize,
          total: result.count,
        });
      } else {
        setError(getLmsErrorMessage(response, "Unable to load LMS sessions"));
      }
      setLoading(false);
    },
    [api, filters],
  );

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
  }, [fetchAcademicYears, fetchTerms]);

  useEffect(() => {
    const loadScopeOptions = async () => {
      const [classResponse, streamResponse] = await Promise.all([
        Utilities.fetchWithAuth(
          "GET",
          Settings.urls.bootstrap.klass,
          undefined,
          navigate,
        ),
        Utilities.fetchWithAuth(
          "GET",
          Settings.urls.bootstrap.stream,
          undefined,
          navigate,
        ),
      ]);
      const classItems = Array.isArray(classResponse?.data)
        ? classResponse.data
        : [];
      const streamItems = Array.isArray(streamResponse?.data)
        ? streamResponse.data
        : [];
      setClasses(classItems);
      setStreams(streamItems);
    };
    loadScopeOptions();
  }, [navigate]);

  useEffect(() => {
    loadSessions(1, pagination.pageSize);
    // Filters are intentionally the trigger for a new first page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.search,
    filters.status,
    filters.session_type,
    filters.academic_year,
    filters.term,
    filters.session_scope,
    filters.school_class,
    filters.stream,
  ]);

  useEffect(() => {
    const sessionId = new URLSearchParams(location.search).get("session");
    if (!sessionId) return;
    const record = sessions.find(
      (item) => String(item.id) === String(sessionId),
    );
    if (record) {
      setDetail(record);
      setDetailOpen(true);
    }
  }, [location.search, sessions]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(sessionFormValues(null));
    setSelectedScope("Whole School");
    setFormOpen(true);
  };

  const openEdit = async (record) => {
    const response = await api.sessions.get(record.id);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to load session"));
      return;
    }
    setEditing(response.data);
    form.resetFields();
    form.setFieldsValue(sessionFormValues(response.data, academicYears, terms));
    setSelectedScope(response.data.sessionScope || "Whole School");
    setFormOpen(true);
  };

  const handleSave = async (values) => {
    const [startDate, endDate] = values.dates || [];
    const body = {
      title: values.title,
      session_type: values.session_type,
      acadyrID: values.acadyrID,
      termID: values.termID,
      start_date: startDate?.format("YYYY-MM-DD"),
      end_date: endDate?.format("YYYY-MM-DD"),
    };
    body.sessionScope = values.sessionScope || "Whole School";
    if (!editing || editing.status !== "Active") {
      if (body.sessionScope === "Selected Classes") {
        body.classScopes = (values.classScopes || []).map((scope) => ({
          classID: scope.classID,
          streamIDs: scope.streamIDs || [],
        }));
      } else if (editing?.sessionScope === "Selected Classes") {
        // PATCH replaces the stored list; clear it when switching to Whole School.
        body.classScopes = [];
      }
    }
    setSaving(true);
    const response = editing
      ? await api.sessions.update(editing.id, body)
      : await api.sessions.create(body);
    setSaving(false);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to save session"));
      return;
    }
    message.success(editing ? "LMS session updated" : "LMS session created");
    setDetail((current) =>
      current?.id === response.data?.id ? response.data : current,
    );
    setFormOpen(false);
    setEditing(null);
    loadSessions(1, pagination.pageSize);
  };

  const handleActivate = async (values) => {
    const body = values.expiresAt
      ? { expiresAt: values.expiresAt.toISOString() }
      : {};
    const response = await api.sessions.activate(activateRecord.id, body);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to activate session"));
      return;
    }
    setActivateRecord(null);
    activateForm.resetFields();
    setUploadKeyResult(response.data?.uploadKey ? response.data : null);
    setDetail((current) =>
      current?.id === activateRecord.id ? response.data : current,
    );
    message.success("LMS session activated");
    loadSessions(1, pagination.pageSize);
  };

  const handleVerify = async (values) => {
    const response = await api.sessions.verifyUploadKey(verifyRecord.id, {
      uploadKey: values.uploadKey,
    });
    if (!response?.ok) {
      message.error(
        getLmsErrorMessage(response, "Unable to verify upload key"),
      );
      return;
    }
    setVerifyRecord(null);
    verifyForm.resetFields();
    await loadSessions(pagination.current, pagination.pageSize);
    if (detail?.id === verifyRecord.id) {
      const refreshed = await api.sessions.get(verifyRecord.id);
      if (refreshed?.ok) setDetail(refreshed.data);
    }
    message.success(
      `Upload access granted until ${formatDateTime(response.data?.expiresAt)}`,
    );
  };

  const handleDeactivate = async (record) => {
    const response = await api.sessions.deactivate(record.id);
    if (!response?.ok) {
      message.error(
        getLmsErrorMessage(response, "Unable to deactivate session"),
      );
      return;
    }
    message.success("LMS session deactivated");
    setDetail((current) =>
      current?.id === record.id ? response.data : current,
    );
    loadSessions(1, pagination.pageSize);
  };

  const handleDelete = async (record) => {
    const response = await api.sessions.remove(record.id);
    if (!response?.ok) {
      const reason = getLmsErrorMessage(response, "Unable to delete session");
      if (response?.status === 400) {
        setArchiveReason(reason);
        setArchiveRecord(record);
      } else {
        message.error(reason);
      }
      return;
    }
    message.success("LMS session deleted");
    if (detail?.id === record.id) {
      setDetailOpen(false);
      setDetail(null);
    }
    loadSessions(1, pagination.pageSize);
  };

  const handleArchive = async () => {
    if (!canArchive(archiveRecord) || archiving) return;
    setArchiving(true);
    try {
      const response = await api.sessions.archive(archiveRecord.id);
      if (!response?.ok) {
        setArchiveReason(
          getLmsErrorMessage(response, "Unable to archive session"),
        );
        return;
      }
      setDetail((current) =>
        current?.id === archiveRecord.id ? response.data : current,
      );
      setArchiveRecord(null);
      setArchiveReason("");
      message.success("LMS session archived");
      await loadSessions(pagination.current, pagination.pageSize);
    } finally {
      setArchiving(false);
    }
  };

  const columns = [
    {
      title: "Session",
      dataIndex: "title",
      key: "title",
      fixed: "left",
      width: 240,
      render: (value, record) => (
        <Button
          type="link"
          style={{
            padding: 0,
            fontWeight: 600,
            maxWidth: "100%",
            whiteSpace: "normal",
            height: "auto",
            textAlign: "left",
            overflowWrap: "anywhere",
          }}
          onClick={() => {
            setDetail(record);
            setDetailOpen(true);
          }}
        >
          {value}
        </Button>
      ),
    },
    {
      title: "Type",
      dataIndex: "session_type",
      key: "session_type",
      width: 190,
    },
    {
      title: "Academic year",
      dataIndex: "academicYear",
      key: "academicYear",
      width: 120,
    },
    { title: "Term", dataIndex: "termName", key: "termName", width: 100 },
    {
      title: "Scope",
      key: "scope",
      width: 260,
      render: (_, record) => sessionScopeDetails(record),
    },
    {
      title: "Dates",
      key: "dates",
      width: 220,
      render: (_, record) =>
        `${formatDate(record.start_date)} – ${formatDate(record.end_date)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => <Tag color={statusColor(status)}>{status}</Tag>,
    },
    {
      title: "Upload key expires at",
      dataIndex: "uploadKeyExpiresAt",
      key: "uploadKeyExpiresAt",
      width: 190,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 290,
      render: (_, record) => (
        <Space size="small">
          {canArchive(record) && (
            <Button
              type="primary"
              icon={<InboxOutlined />}
              onClick={() => {
                setArchiveReason("");
                setArchiveRecord(record);
              }}
            >
              Archive
            </Button>
          )}
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              setDetail(record);
              setDetailOpen(true);
            }}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          />
          {record.status === "Pending Approval" && (
            <Button
              type="text"
              icon={<CheckCircleOutlined />}
              style={{ color: "#52c41a" }}
              onClick={() => setActivateRecord(record)}
            />
          )}
          {record.status === "Active" && (
            <Popconfirm
              title="Deactivate this LMS session?"
              onConfirm={() => handleDeactivate(record)}
            >
              <Button type="text" icon={<StopOutlined />} danger />
            </Popconfirm>
          )}
          {record.status !== "Active" && record.status !== "Archived" && (
            <Popconfirm
              title="Delete this LMS session?"
              description="This cannot be undone."
              onConfirm={() => handleDelete(record)}
            >
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Popconfirm>
          )}
          {record.status === "Active" &&
            record.uploadKeyExpiresAt &&
            !record.uploadKeyVerified && (
              <Button
                type="text"
                icon={<KeyOutlined />}
                onClick={() => setVerifyRecord(record)}
              />
            )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <LmsPageHeader
        title="LMS sessions"
        description="Set up and approve the special learning periods that hold your content, live classes and learner activity."
        extra={[
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => loadSessions(1, pagination.pageSize)}
          >
            Refresh
          </Button>,
          <Button
            key="new"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
          >
            New session
          </Button>,
        ]}
      />
      <LmsErrorAlert error={error} onClose={() => setError("")} />

      <Card>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={7} lg={6}>
            <Input.Search
              allowClear
              placeholder="Search sessions"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              onSearch={(value) =>
                setFilters((current) => ({ ...current, search: value }))
              }
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Status"
              value={filters.status}
              onChange={(value) =>
                setFilters((current) => ({ ...current, status: value }))
              }
              options={LMS_SESSION_STATUSES.map((value) => ({
                value,
                label: value,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Session type"
              value={filters.session_type}
              onChange={(value) =>
                setFilters((current) => ({ ...current, session_type: value }))
              }
              options={LMS_SESSION_TYPES.map((value) => ({
                value,
                label: value,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder="Academic year"
              value={filters.academic_year}
              onChange={(value) =>
                setFilters((current) => ({ ...current, academic_year: value }))
              }
              options={academicYears.map((year) => ({
                value: year.id,
                label: year.acadyr,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder="Term"
              value={filters.term}
              onChange={(value) =>
                setFilters((current) => ({ ...current, term: value }))
              }
              options={terms.map((term) => ({
                value: term.id,
                label: term.name,
              }))}
            />
          </Col>
        </Row>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="All scopes"
              value={filters.session_scope}
              onChange={(value) =>
                setFilters((current) => ({ ...current, session_scope: value }))
              }
              options={["Whole School", "Selected Classes"].map((value) => ({
                value,
                label: value,
              }))}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder="All classes"
              value={filters.school_class}
              onChange={(value) =>
                setFilters((current) => ({ ...current, school_class: value }))
              }
              options={classes.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder="All streams"
              value={filters.stream}
              onChange={(value) =>
                setFilters((current) => ({ ...current, stream: value }))
              }
              options={streams.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            />
          </Col>
        </Row>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={sessions}
          loading={loading}
          scroll={{ x: 1840 }}
          tableLayout="fixed"
          pagination={{
            ...pagination,
            showSizeChanger: false,
            showTotal: (total) => `${total} session${total === 1 ? "" : "s"}`,
          }}
          onChange={(nextPagination) =>
            loadSessions(nextPagination.current, nextPagination.pageSize)
          }
        />
      </Card>

      <Modal
        title={editing ? "Edit LMS session" : "New LMS session"}
        open={formOpen}
        onCancel={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="sessionScope"
            label="Session scope"
            rules={[{ required: true }]}
          >
            <Select
              disabled={editing?.status === "Active"}
              options={[
                { value: "Whole School", label: "Whole School" },
                { value: "Selected Classes", label: "Selected Classes" },
              ]}
              onChange={(value) => {
                setSelectedScope(value);
                form.setFieldValue(
                  "classScopes",
                  value === "Selected Classes" ? [{ streamIDs: [] }] : [],
                );
              }}
            />
          </Form.Item>
          {selectedScope === "Selected Classes" && (
            <SessionClassScopes
              form={form}
              classes={classes}
              streams={streams}
              disabled={editing?.status === "Active"}
            />
          )}
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Enter a session title" }]}
          >
            <Input placeholder="e.g. 2026 PLE Revision Week" />
          </Form.Item>
          <Form.Item
            name="session_type"
            label="Session type"
            rules={[{ required: true, message: "Select a session type" }]}
          >
            <Select
              options={LMS_SESSION_TYPES.map((value) => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="acadyrID"
                label="Academic year"
                rules={[{ required: true, message: "Select an academic year" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={academicYears.map((year) => ({
                    value: year.id,
                    label: year.acadyr,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="termID"
                label="Term"
                rules={[{ required: true, message: "Select a term" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={terms.map((term) => ({
                    value: term.id,
                    label: term.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="dates"
            label="Session dates"
            rules={[{ required: true, message: "Choose start and end dates" }]}
          >
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Activate ${activateRecord?.title || "session"}`}
        open={Boolean(activateRecord)}
        onCancel={() => {
          setActivateRecord(null);
          activateForm.resetFields();
        }}
        onOk={() => activateForm.submit()}
        destroyOnClose
      >
        <Alert
          message="Activation changes the session status to Active."
          description="Set an expiry only if teachers should receive a temporary upload key. Leave it blank to activate without a key."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={activateForm} layout="vertical" onFinish={handleActivate}>
          <Form.Item name="expiresAt" label="Upload key expires at">
            <DatePicker
              showTime
              style={{ width: "100%" }}
              disabledDate={(date) => date && date.isBefore(dayjs(), "day")}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Upload key generated"
        open={Boolean(uploadKeyResult)}
        onCancel={() => setUploadKeyResult(null)}
        footer={
          <Button type="primary" onClick={() => setUploadKeyResult(null)}>
            Done
          </Button>
        }
      >
        <Alert
          type="warning"
          showIcon
          message="Copy this key now. It will not be shown again."
          description={`Expires ${formatDateTime(uploadKeyResult?.uploadKeyExpiresAt)}`}
        />
        <Input
          value={uploadKeyResult?.uploadKey || ""}
          readOnly
          addonAfter={
            <CopyOutlined
              onClick={() =>
                navigator.clipboard?.writeText(uploadKeyResult?.uploadKey || "")
              }
            />
          }
          style={{ marginTop: 16, fontWeight: 600, letterSpacing: 2 }}
        />
      </Modal>

      <Modal
        title={`Verify upload access${verifyRecord ? ` · ${verifyRecord.title}` : ""}`}
        open={Boolean(verifyRecord)}
        onCancel={() => {
          setVerifyRecord(null);
          verifyForm.resetFields();
        }}
        onOk={() => verifyForm.submit()}
        destroyOnClose
      >
        <Form form={verifyForm} layout="vertical" onFinish={handleVerify}>
          <Form.Item
            name="uploadKey"
            label="Upload key"
            rules={[{ required: true, message: "Enter the upload key" }]}
          >
            <Input.Password placeholder="Enter the teacher upload key" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Archive ${archiveRecord?.title || "session"}?`}
        open={Boolean(archiveRecord)}
        onCancel={() => {
          if (!archiving) {
            setArchiveRecord(null);
            setArchiveReason("");
          }
        }}
        onOk={handleArchive}
        okText="Archive"
        okButtonProps={{ disabled: !canArchive(archiveRecord) }}
        confirmLoading={archiving}
        cancelButtonProps={{ disabled: archiving }}
        closable={!archiving}
        maskClosable={!archiving}
        keyboard={!archiving}
      >
        {archiveReason && (
          <Alert
            type="warning"
            showIcon
            message={archiveReason}
            style={{ marginBottom: 16 }}
          />
        )}
        <Typography.Paragraph>
          Archiving preserves the session's content and history. Learners can
          still access it as past content.
        </Typography.Paragraph>
        {archiveRecord && !canArchive(archiveRecord) && (
          <Alert
            type="info"
            showIcon
            message="Only Completed or Inactive sessions can be archived."
          />
        )}
      </Modal>

      <Drawer
        title={
          <Title level={5} style={{ margin: 0 }}>
            Session details
          </Title>
        }
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={560}
      >
        {detail && (
          <>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Title">
                {detail.title}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {detail.session_type}
              </Descriptions.Item>
              <Descriptions.Item label="Scope">
                {sessionScopeDetails(detail)}
              </Descriptions.Item>
              <Descriptions.Item label="Academic year">
                {detail.academicYear}
              </Descriptions.Item>
              <Descriptions.Item label="Term">
                {detail.termName}
              </Descriptions.Item>
              <Descriptions.Item label="Dates">
                {formatDate(detail.start_date)} – {formatDate(detail.end_date)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColor(detail.status)}>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Initiated by">
                {detail.initiatedByName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Approved by">
                {detail.approvedByName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Approved at">
                {formatDateTime(detail.approvedAt)}
              </Descriptions.Item>
              {detail.status === "Archived" && (
                <Descriptions.Item label="Archived by">
                  {detail.archivedByName || "—"}
                </Descriptions.Item>
              )}
              {detail.status === "Archived" && (
                <Descriptions.Item label="Archived at">
                  {formatDateTime(detail.archivedAt)}
                </Descriptions.Item>
              )}
              {!detail.uploadKeyVerified && (
                <Descriptions.Item label="Upload key expires">
                  {formatDateTime(detail.uploadKeyExpiresAt)}
                </Descriptions.Item>
              )}
            </Descriptions>
            <Text type="secondary" style={{ display: "block", marginTop: 16 }}>
              Created {formatDateTime(detail.createdAt)}
            </Text>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default LmsSessions;
